import {
  Box,
  Button,
  Checkbox,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  Portal,
  Stack,
  VStack,
} from '@chakra-ui/react';
import { useState } from 'react';
import { MdOutlineKeyboardArrowDown } from 'react-icons/md';
import { IoClose } from 'react-icons/io5';
import { DtType } from '../../generated/graphql';
import { HelivanText } from '../helivan/HelivanText';
import { HelivanColor } from '../helivan/HelivanColor';

const getDemographics = (dtType: DtType): Record<string, string[]> => {
  if (dtType === DtType.Congressperson) {
    return {
      party: ['Democrat', 'Republican', 'Independent'],
      chamber: ['house', 'senate'],
      state: [
        'AL',
        'AK',
        'AZ',
        'AR',
        'CA',
        'CO',
        'CT',
        'DE',
        'FL',
        'GA',
        'HI',
        'ID',
        'IL',
        'IN',
        'IA',
        'KS',
        'KY',
        'LA',
        'ME',
        'MD',
        'MA',
        'MI',
        'MN',
        'MS',
        'MO',
        'MT',
        'NE',
        'NV',
        'NH',
        'NJ',
        'NM',
        'NY',
        'NC',
        'ND',
        'OH',
        'OK',
        'OR',
        'PA',
        'RI',
        'SC',
        'SD',
        'TN',
        'TX',
        'UT',
        'VT',
        'VA',
        'WA',
        'WV',
        'WI',
        'WY',
      ],
    };
  } else if (dtType === DtType.MoviesSimple) {
    return {
      gender: ['Female', 'Male'],
    };
  } else if (dtType === DtType.MoviesFull || dtType === DtType.MoviesMedium) {
    return {
      gender: ['Female', 'Male'],
      ethnicity: [
        'American Indian or Alaska Native',
        'Asian',
        'Black or African American',
        'Hispanic',
        'Pacific Islander',
        'White',
      ],
      region: ['Midwest', 'Northeast', 'South', 'West'],
    };
  } else if (dtType === DtType.EarlyStageVcs) {
    return {
      location: [
        'Atlanta',
        'Austin',
        'Europe',
        'Boston',
        'Chicago',
        'Israel',
        'Los Angeles',
        'Miami',
        'New York',
        'Philadelphia',
        'San Francisco / Bay Area',
        'Seattle',
        'London',
        'Washington, DC',
      ],
    };
  } else if (dtType === DtType.FortuneCeos) {
    return {
      industry: [
        'Consumer Goods & Retail',
        'Energy & Utilities',
        'Financial Services',
        'Health Care & Pharmaceuticals',
        'Industrial & Manufacturing',
        'Media, Entertainment & Publishing',
        'Real Estate & Construction',
        'Technology & IT Services',
        'Transportation & Logistics',
        'Waste, Environmental & Other Services',
      ],
    };
  }

  return {};
};

// Helper function to capitalize category names
const capitalizeCategory = (key: string): string => {
  return key.charAt(0).toUpperCase() + key.slice(1);
};

// Helper function to get display text for values
const getValueDisplay = (key: string, value: string): string => {
  if (key === 'chamber') {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }
  if (key === 'state') {
    const stateMap: Record<string, string> = {
      AL: 'Alabama',
      AK: 'Alaska',
      AZ: 'Arizona',
      AR: 'Arkansas',
      CA: 'California',
      CO: 'Colorado',
      CT: 'Connecticut',
      DE: 'Delaware',
      FL: 'Florida',
      GA: 'Georgia',
      HI: 'Hawaii',
      ID: 'Idaho',
      IL: 'Illinois',
      IN: 'Indiana',
      IA: 'Iowa',
      KS: 'Kansas',
      KY: 'Kentucky',
      LA: 'Louisiana',
      ME: 'Maine',
      MD: 'Maryland',
      MA: 'Massachusetts',
      MI: 'Michigan',
      MN: 'Minnesota',
      MS: 'Mississippi',
      MO: 'Missouri',
      MT: 'Montana',
      NE: 'Nebraska',
      NV: 'Nevada',
      NH: 'New Hampshire',
      NJ: 'New Jersey',
      NM: 'New Mexico',
      NY: 'New York',
      NC: 'North Carolina',
      ND: 'North Dakota',
      OH: 'Ohio',
      OK: 'Oklahoma',
      OR: 'Oregon',
      PA: 'Pennsylvania',
      RI: 'Rhode Island',
      SC: 'South Carolina',
      SD: 'South Dakota',
      TN: 'Tennessee',
      TX: 'Texas',
      UT: 'Utah',
      VT: 'Vermont',
      VA: 'Virginia',
      WA: 'Washington',
      WV: 'West Virginia',
      WI: 'Wisconsin',
      WY: 'Wyoming',
    };
    return stateMap[value] || value;
  }
  return value;
};

interface DemographicFilterSelectProps {
  dtType: DtType;
  selectedDemographics?: Set<{ key: string; value: string }>;
  setSelectedDemographics: (d?: Set<{ key: string; value: string }>) => void;
}

export const DemographicFilterSelect = ({
  dtType,
  selectedDemographics = new Set(),
  setSelectedDemographics,
}: DemographicFilterSelectProps) => {
  const demographics = getDemographics(dtType);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Helper function to check if a demographic is selected
  const isDemographicSelected = (key: string, value: string): boolean => {
    return Array.from(selectedDemographics).some(
      (item) => item.key === key && item.value === value
    );
  };
  // Toggle a demographic selection
  const toggleDemographic = (key: string, value: string) => {
    const newSelectedDemographics = new Set(selectedDemographics);

    // Check if we have an existing item with the same key
    const existingItem = Array.from(newSelectedDemographics).find(
      (item) => item.key === key
    );

    // Remove all items in this category first
    Array.from(newSelectedDemographics)
      .filter((item) => item.key === key)
      .forEach((item) => newSelectedDemographics.delete(item));

    // Only add the new item if it wasn't the one that was previously selected
    if (!existingItem || existingItem.value !== value) {
      newSelectedDemographics.add({ key, value });
    }

    setSelectedDemographics(
      newSelectedDemographics.size > 0 ? newSelectedDemographics : undefined
    );
  };

  // Create display text for the button
  const getButtonText = () => {
    if (!selectedDemographics || selectedDemographics.size === 0) {
      return 'Demographic';
    }

    if (selectedDemographics.size === 1) {
      const item = Array.from(selectedDemographics)[0];
      return `${getValueDisplay(item.key, item.value)}`;
    }

    return Array.from(selectedDemographics)
      .map((item) => getValueDisplay(item.key, item.value))
      .join(', ');
  };

  return Object.keys(demographics).length === 0 ? undefined : (
    <Menu
      closeOnSelect={false}
      isLazy
      onOpen={() => {
        setIsMenuOpen(true);
      }}
      onClose={() => {
        setIsMenuOpen(false);
      }}
      isOpen={isMenuOpen}
    >
      <MenuButton
        as={Button}
        rightIcon={
          selectedDemographics.size > 0 ? (
            <IoClose
              size={14}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedDemographics(undefined);
              }}
            />
          ) : (
            <MdOutlineKeyboardArrowDown size={16} />
          )
        }
        size="sm"
        variant="outline"
        bg="white"
        border="1px solid"
        borderColor={HelivanColor.border.primary}
        borderRadius="6px"
        fontWeight={500}
        fontSize="13px"
        color={selectedDemographics.size > 0 ? HelivanColor.text.blueDark : HelivanColor.text.blueMid}
        _hover={{ bg: HelivanColor.background.secondary }}
        _active={{ bg: HelivanColor.background.secondary }}
      >
        {getButtonText()}
      </MenuButton>

      <Portal>
        <MenuList
          p={3}
          width="280px"
          zIndex={1400}
          boxShadow="0 4px 20px rgba(0,0,0,0.15)"
          borderRadius="8px"
          border="1px solid"
          borderColor={HelivanColor.border.primary}
        >
          <Box maxH="400px" overflowY="auto">
            <VStack align="stretch" spacing={3}>
              {Object.entries(demographics).map(([key, values], idx) => (
                <Stack key={idx} spacing={1}>
                  <HelivanText variant="smallBody+" colorOverride={HelivanColor.text.blueMid}>
                    {capitalizeCategory(key)}
                  </HelivanText>
                  {values.map((val, idx2) => {
                    const isSelected = isDemographicSelected(key, val);
                    return (
                      <Flex
                        key={idx2}
                        width="full"
                        justifyContent="space-between"
                        alignItems="center"
                        _hover={{ cursor: 'pointer', bg: HelivanColor.background.secondary }}
                        onClick={() => toggleDemographic(key, val)}
                        py="6px"
                        px="8px"
                        borderRadius="6px"
                        bg={isSelected ? HelivanColor.hover.lightBlue : 'white'}
                      >
                        <HelivanText variant="smallBody" colorOverride={HelivanColor.text.blueDark}>
                          {getValueDisplay(key, val)}
                        </HelivanText>
                        <Checkbox
                          isChecked={isSelected}
                          colorScheme="blue"
                          size="sm"
                          pointerEvents="none"
                        />
                      </Flex>
                    );
                  })}
                </Stack>
              ))}
            </VStack>
          </Box>
        </MenuList>
      </Portal>
    </Menu>
  );
};
